// frontend/src/main.js - Fixed Vue 3 Application Entry Point
import { createApp } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import App from "./App.vue";
import router from "./router";
import en from "./locales/en.js";
import ar from "./locales/ar.js";

// Import global styles
import "./assets/main.css";

// i18n
const savedLocale = localStorage.getItem("exotrack_locale") || "en";
const i18n = createI18n({
  legacy: false,
  locale: savedLocale,
  fallbackLocale: "en",
  messages: { en, ar },
});

// Create Vue application instance
const app = createApp(App);

// Create Pinia store instance
const pinia = createPinia();

// Install plugins
app.use(pinia);
app.use(router);
app.use(i18n);

// Global error handler for better debugging
app.config.errorHandler = (error, instance, info) => {
  console.error("Global Vue Error:", {
    error: error,
    component: instance,
    info: info,
  });

  // Handle specific error types
  if (error.name === "ChunkLoadError") {
    console.warn("Chunk load error detected, reloading page...");
    window.location.reload();
    return;
  }

  // Handle authentication errors
  if (error.response?.status === 401) {
    console.warn("Authentication error detected");
    // Import auth store dynamically to avoid circular imports
    import("./stores/auth.js")
      .then(({ useAuthStore }) => {
        const authStore = useAuthStore();
        authStore.clearAuth();
        router.push("/login");
      })
      .catch((err) => {
        console.error("Failed to handle auth error:", err);
      });
  }
};

// Global warning handler
app.config.warnHandler = (msg, instance, trace) => {
  console.warn("Vue Warning:", msg);
  if (import.meta.env.DEV) {
    console.warn("Component trace:", trace);
  }
};

// Set up global properties
app.config.globalProperties.$log = console.log;

// Activity tracking for session management
let activityTimer;
let sessionCheckInterval;

const initializeActivityTracking = () => {
  // Activity tracking events
  const activityEvents = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart",
  ];

  const throttledActivity = throttle(() => {
    // Import auth store dynamically
    import("./stores/auth.js")
      .then(({ useAuthStore }) => {
        const authStore = useAuthStore();
        if (authStore.isAuthenticated) {
          authStore.updateActivity();
        }
      })
      .catch((err) => {
        console.error("Failed to update activity:", err);
      });
  }, 30000); // Update activity max once per 30 seconds

  activityEvents.forEach((event) => {
    document.addEventListener(event, throttledActivity, true);
  });
};

// Throttle utility function
function throttle(func, limit) {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// Initialize app function
const initializeApp = async () => {
  try {
    // Start activity tracking
    initializeActivityTracking();

    console.log("Application initialized successfully");
  } catch (error) {
    console.error("Failed to initialize application:", error);
  }
};

// Development helpers
if (import.meta.env.DEV) {
  // Make stores available globally for debugging
  window.__app__ = app;
  window.__router__ = router;
  window.__pinia__ = pinia;

  console.log("Vue app is ready for debugging");
}

// Cleanup on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    if (activityTimer) clearTimeout(activityTimer);
    if (sessionCheckInterval) clearInterval(sessionCheckInterval);
  });
}

// Mount the application
const mountApp = () => {
  try {
    app.mount("#app");
    console.log("Vue application mounted successfully");

    // Initialize app after mounting
    initializeApp();
  } catch (error) {
    console.error("Failed to mount Vue application:", error);

    // Show error message to user
    const appElement = document.getElementById("app");
    if (appElement) {
      appElement.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          font-family: sans-serif;
          text-align: center;
          padding: 20px;
        ">
          <h1 style="color: #e74c3c; margin-bottom: 16px;">
            ⚠️ Application Error
          </h1>
          <p style="color: #666; margin-bottom: 20px;">
            Failed to load the application. Please refresh the page or contact support.
          </p>
          <button 
            onclick="window.location.reload()" 
            style="
              background: #3498db;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 16px;
            "
          >
            🔄 Reload Page
          </button>
        </div>
      `;
    }
  }
};

// Start the application
mountApp();
